/**
 * Achicar la foto ANTES de mandarla.
 *
 * ── Por qué ──
 *
 * El servidor tiene 1 GB de memoria con la base de datos adentro, y las fotos se guardan
 * dentro de la base. Una foto de celular sin tocar pesa 2 a 4 MB, y convertida a texto para
 * viajar crece un 33% más. Tres fotos por visita, cinco visitas al día, y en un mes la base
 * pesa más que todo lo demás junto — incluida la copia de seguridad de las 3 a.m., que tiene
 * que caber en el mismo servidor.
 *
 * Achicada a 1280 px de lado y 70% de calidad, la misma foto pesa 150 a 400 KB y se ve igual
 * de bien en la pantalla de un celular. Lo que se pierde es detalle que nadie iba a mirar.
 *
 * Y el técnico está en la calle: mandar 4 MB por datos móviles en una vereda es la diferencia
 * entre guardar el trabajo y quedarse esperando.
 *
 * El servidor comprueba el peso otra vez por su cuenta. Esto es una comodidad, no una
 * defensa: el que manda la foto es el que decide su tamaño.
 */

/** El lado más largo que puede tener la foto ya achicada. */
export const LADO_MAX = 1280;

/** Y con cuánta calidad se guarda. 0,7 es donde deja de notarse la diferencia. */
export const CALIDAD = 0.7;

/**
 * Lee el archivo que escogió el celular y devuelve la foto achicada, lista para mandar.
 *
 * @param {File} archivo lo que entrega el `<input type="file" capture>`
 * @returns {Promise<{base64:string, mime:string, peso:number}>}
 */
export async function achicar(archivo, ladoMax = LADO_MAX, calidad = CALIDAD) {
  const imagen = await leerImagen(archivo);
  const { width: anchoOriginal, height: altoOriginal } = imagen;

  /* Se achica por el lado más largo, así una foto vertical y una horizontal quedan del mismo
     tamaño. Y nunca se AGRANDA: una foto que ya venía pequeña se deja como está — estirarla
     no le agrega detalle, solo peso. */
  const escala = Math.min(1, ladoMax / Math.max(anchoOriginal, altoOriginal));
  const ancho = Math.round(anchoOriginal * escala);
  const alto = Math.round(altoOriginal * escala);

  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;
  const pincel = lienzo.getContext('2d');
  /* Fondo blanco: un PNG con transparencia pasado a JPEG deja las zonas transparentes en
     negro, y una foto de un trabajo con manchas negras parece un defecto de la cámara. */
  pincel.fillStyle = '#FFFFFF';
  pincel.fillRect(0, 0, ancho, alto);
  pincel.drawImage(imagen, 0, 0, ancho, alto);
  if (imagen.close) imagen.close();

  const base64 = lienzo.toDataURL('image/jpeg', calidad);
  return {
    base64,
    mime: 'image/jpeg',
    // El peso de lo que de verdad va a viajar, sin el «data:image/jpeg;base64,» del principio.
    peso: base64.length - (base64.indexOf(',') + 1),
  };
}

/**
 * Abre el archivo como imagen.
 *
 * `createImageBitmap` con `imageOrientation: 'from-image'` respeta la orientación que guarda
 * el celular. Sin eso, las fotos tomadas con el teléfono de lado salen acostadas: el celular
 * no gira los píxeles, escribe aparte «esta va girada», y el lienzo ignora esa nota.
 *
 * Si el navegador no lo soporta se cae al método de siempre, que sí puede acostarlas — más
 * vale una foto acostada que ninguna.
 */
async function leerImagen(archivo) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(archivo, { imageOrientation: 'from-image' });
    } catch (e) { /* sigue por el otro camino */ }
  }
  return new Promise((listo, falla) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); listo(img); };
    img.onerror = () => { URL.revokeObjectURL(url); falla(new Error('No se pudo leer la foto')); };
    img.src = url;
  });
}

/** Para enseñar pesos en pantalla sin que nadie tenga que dividir por mil. */
export const enKb = (bytes) => Math.round(bytes / 1024) + ' KB';
